import React from 'react';
import { ExclamationTriangleIcon, DocumentTextIcon } from '@heroicons/react/24/outline';

const CMS_REQUIRED_HEADERS = [
  "Component",
  "Activity Type", 
  "Compliance or FWA?",
  "Activity Frequency",
  "Activity Rationale",
  "Activity Description",
  "Activity Start Date",
  "Activity Completion Date",
  "Number of Deficiencies",
  "Description of Deficiencies",
  "Corrective Action Required",
  "Activity Results Shared?"
];

export default function CPEComplianceError({ headerErrors, actualHeaders }) {
  const complianceError = headerErrors.find(err => err.field === 'COMPLIANCE REQUIREMENT');
  const fieldErrors = headerErrors.filter(err => err.field !== 'COMPLIANCE REQUIREMENT');

  return (
    <div className="bg-red-50/90 border border-red-200/60 rounded-2xl p-6 backdrop-blur-xl">
      <div className="flex items-start gap-4 mb-6">
        <div className="w-12 h-12 rounded-xl bg-gradient-to-r from-red-500 to-red-600 flex items-center justify-center shadow-lg">
          <ExclamationTriangleIcon className="w-6 h-6 text-white" />
        </div>
        
        <div className="flex-1">
          <h3 className="text-lg font-bold text-red-900 mb-2">
            CMS CPE COA Header Compliance Error
          </h3>
          {complianceError && (
            <p className="text-red-800 text-sm leading-relaxed mb-4">
              {complianceError.message}
            </p>
          )}
        </div>
      </div>

      {/* Field-specific errors */}
      {fieldErrors.length > 0 && (
        <div className="mb-6">
          <h4 className="text-base font-semibold text-red-900 mb-3">Specific Issues Found:</h4>
          <div className="space-y-2">
            {fieldErrors.map((error, index) => (
              <div key={index} className="bg-red-100/50 border border-red-200/50 rounded-lg p-3">
                <div className="flex items-start gap-2">
                  <div className="w-2 h-2 bg-red-500 rounded-full mt-2 flex-shrink-0"></div>
                  <div>
                    <span className="font-medium text-red-900">{error.field}:</span>
                    <span className="text-red-800 ml-2">{error.message}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Required headers reference */}
      <div className="bg-white/80 border border-red-200/50 rounded-xl p-4">
        <div className="flex items-center gap-3 mb-3">
          <DocumentTextIcon className="w-5 h-5 text-red-700" />
          <h4 className="text-base font-semibold text-red-900">
            Required CMS CPE COA Universe Table Headers (Columns A-L):
          </h4>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
          {CMS_REQUIRED_HEADERS.map((header, index) => (
            <div key={index} className="flex items-center gap-2">
              <span className="w-6 h-6 bg-red-100 text-red-700 text-xs font-bold rounded flex items-center justify-center">
                {String.fromCharCode(65 + index)}
              </span>
              <code className="text-red-800 font-mono text-xs bg-red-50 px-2 py-1 rounded">
                {header}
              </code>
            </div>
          ))}
        </div>
      </div>

      {/* Your headers vs required */}
      {actualHeaders && actualHeaders.length > 0 && (
        <div className="mt-4 bg-slate-50/80 border border-slate-200/50 rounded-xl p-4">
          <h4 className="text-base font-semibold text-slate-900 mb-3">Your File Headers:</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
            {actualHeaders.map((header, index) => {
              const isCorrect = header === CMS_REQUIRED_HEADERS[index];
              return (
                <div key={index} className={`flex items-center gap-2 ${isCorrect ? 'text-green-700' : 'text-red-700'}`}>
                  <span className={`w-6 h-6 text-xs font-bold rounded flex items-center justify-center ${
                    isCorrect ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                  }`}>
                    {String.fromCharCode(65 + index)}
                  </span>
                  <code className={`font-mono text-xs px-2 py-1 rounded ${
                    isCorrect ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
                  }`}>
                    {header}
                  </code>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="mt-4 text-xs text-red-700 bg-red-100/50 rounded-lg p-3">
        <strong>Compliance Note:</strong> Headers must match exactly as specified in the CMS Program Audit Protocol & Data Request — Medicare Parts C & D CPE, Universe Table 1: COA (pages 12-15). Case-sensitive, exact spelling, and correct column order (A-L) are required for successful audit submission.
      </div>
    </div>
  );
}
